import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Image as ImageIcon, FileText, Search, Send } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useWhatsappTemplates, useWhatsappWebMutations, whatsappError, type WhatsappTemplate } from '../whatsappWebApi';

interface TemplatePickerProps {
  open: boolean;
  onClose: () => void;
  chatId: string;
  /** Used to pre-fill a template's first value ({{1}} is usually the name). */
  contactName?: string;
}

function fill(body: string, values: string[]) {
  return body.replace(/\{\{(\d+)\}\}/g, (_, n) => values[Number(n) - 1]?.trim() || `{{${n}}}`);
}

const HEADER_LABEL: Record<string, string> = { IMAGE: 'Picture', VIDEO: 'Video', DOCUMENT: 'Document' };

/** Choose one of the account's WhatsApp-approved templates and send it. */
export function TemplatePicker({ open, onClose, chatId, contactName = '' }: TemplatePickerProps) {
  const { data: templates = [], isLoading, error } = useWhatsappTemplates(open);
  const { sendTemplate } = useWhatsappWebMutations();
  const [query, setQuery] = useState('');
  const [chosen, setChosen] = useState<WhatsappTemplate | null>(null);
  const [values, setValues] = useState<string[]>([]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Document templates (quotation / proforma / invoice) are sent from the
    // document itself, with its own PDF — not from the inbox.
    const usable = templates.filter((t) => t.headerFormat !== 'DOCUMENT');
    return q ? usable.filter((t) => `${t.name} ${t.body}`.toLowerCase().includes(q)) : usable;
  }, [templates, query]);

  const choose = (t: WhatsappTemplate) => {
    setChosen(t);
    const first = contactName.replace(/\s*\(.*\)\s*$/, '').trim();
    setValues(Array.from({ length: t.varCount }, (_, i) => (i === 0 && first && !/^\+?\d[\d\s]+$/.test(first) ? first : '')));
  };

  const close = () => { setChosen(null); setQuery(''); onClose(); };

  const send = () => {
    if (!chosen) return;
    if (values.some((v) => !v.trim())) { toast.error('Fill in every value.'); return; }
    sendTemplate.mutate({ to: chatId, templateName: chosen.name, values }, {
      onSuccess: () => { toast.success('Template sent.'); close(); },
      onError: (err) => toast.error(whatsappError(err, 'Could not send the template.')),
    });
  };

  return (
    <Modal
      open={open}
      onClose={close}
      size="lg"
      title={chosen ? `Template · ${chosen.name}` : 'Send an approved template'}
      footer={chosen ? (
        <div className="flex w-full items-center justify-between gap-2">
          <Button variant="outline" onClick={() => setChosen(null)}><ArrowLeft className="mr-1 h-4 w-4" /> All templates</Button>
          <Button onClick={send} disabled={sendTemplate.isPending}>
            {sendTemplate.isPending ? <Spinner className="mr-2 h-4 w-4" /> : <Send className="mr-2 h-4 w-4" />} Send
          </Button>
        </div>
      ) : undefined}
    >
      {!chosen ? (
        <div className="flex flex-col gap-3">
          <p className="text-body-sm text-on-surface-variant">
            WhatsApp allows only templates it has approved when the customer has not messaged in the last 24 hours.
            Quotations, proformas and invoices are sent from the document's own Send button, with its PDF.
          </p>
          <label className="flex items-center gap-2 rounded-lg border border-outline px-3 py-2">
            <Search className="h-4 w-4 text-on-surface-variant" aria-hidden />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search templates" className="w-full bg-transparent text-body-md outline-none" />
          </label>
          {isLoading && <div className="flex justify-center py-8"><Spinner /></div>}
          {error && <p className="text-body-sm text-danger-text">{whatsappError(error, 'Could not load the templates.')}</p>}
          <div className="flex max-h-[55vh] flex-col gap-2 overflow-y-auto">
            {list.map((t) => (
              <button
                key={`${t.name}-${t.language}`}
                type="button"
                onClick={() => choose(t)}
                className="flex gap-3 rounded-lg border border-outline p-3 text-left hover:border-primary hover:bg-primary/5"
              >
                {t.sampleMediaUrl && t.headerFormat === 'IMAGE' ? (
                  <img src={t.sampleMediaUrl} alt="" className="h-14 w-14 shrink-0 rounded object-cover" loading="lazy" />
                ) : (
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded bg-surface-variant text-on-surface-variant">
                    {t.headerFormat === 'DOCUMENT' ? <FileText className="h-6 w-6" /> : <span className="text-title-sm">Aa</span>}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-on-surface">{t.name}</span>
                    <span className="rounded bg-surface-variant px-1.5 py-0.5 text-[11px] uppercase text-on-surface-variant">{t.category.toLowerCase() || 'template'}</span>
                    {t.varCount > 0 && <span className="text-[11px] text-on-surface-variant">{t.varCount} value(s) to fill</span>}
                  </div>
                  <p className="mt-1 line-clamp-2 text-body-sm text-on-surface-variant">{t.body.replace(/\*/g, '')}</p>
                </div>
              </button>
            ))}
            {!isLoading && !error && list.length === 0 && <p className="py-6 text-center text-body-sm text-on-surface-variant">No templates match.</p>}
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-3">
            {chosen.varCount === 0 && <p className="text-body-sm text-on-surface-variant">This template has nothing to fill in.</p>}
            {values.map((v, i) => (
              <label key={i} className="flex flex-col gap-1">
                <span className="text-label-md text-on-surface-variant">Value {`{{${i + 1}}}`}</span>
                <input
                  value={v}
                  onChange={(e) => setValues(values.map((x, j) => (j === i ? e.target.value : x)))}
                  className="rounded-lg border border-outline px-3 py-2 text-body-md outline-none focus:border-primary"
                  maxLength={500}
                />
              </label>
            ))}
            {HEADER_LABEL[chosen.headerFormat] && (
              <p className="text-body-sm text-on-surface-variant">
                {HEADER_LABEL[chosen.headerFormat]} sent with it: the one saved on this template in Interakt
                {chosen.sampleFileName ? ` (${chosen.sampleFileName})` : ''}.
              </p>
            )}
          </div>
          <div className="rounded-lg bg-[#efeae2] p-3">
            <div className="rounded-[7.5px] rounded-tr-none bg-[#d9fdd3] p-2 shadow-sm">
              {chosen.headerFormat === 'IMAGE' && chosen.sampleMediaUrl && (
                <img src={chosen.sampleMediaUrl} alt="" className="mb-2 max-h-48 w-full rounded object-cover" />
              )}
              {chosen.headerFormat === 'DOCUMENT' && (
                <div className="mb-2 flex items-center gap-2 rounded bg-black/5 p-2 text-body-sm"><FileText className="h-4 w-4" /> {chosen.sampleFileName || 'Document'}</div>
              )}
              {chosen.headerFormat === 'VIDEO' && (
                <div className="mb-2 flex items-center gap-2 rounded bg-black/5 p-2 text-body-sm"><ImageIcon className="h-4 w-4" /> Video</div>
              )}
              {chosen.headerFormat === 'TEXT' && chosen.headerText && <p className="mb-1 font-semibold">{chosen.headerText}</p>}
              <p className="whitespace-pre-wrap text-[14px] leading-[19px] text-[#111b21]">{fill(chosen.body, values)}</p>
              {chosen.footer && <p className="mt-1 text-[12px] text-[#667781]">{chosen.footer}</p>}
              {chosen.buttons.length > 0 && (
                <div className="mt-2 flex flex-col gap-1 border-t border-black/10 pt-2">
                  {chosen.buttons.map((b) => <span key={b} className="text-center text-[13px] font-medium text-[#00a5f4]">{b}</span>)}
                </div>
              )}
            </div>
            <p className="mt-2 text-center text-[11px] text-[#667781]">Preview of what the customer receives</p>
          </div>
        </div>
      )}
    </Modal>
  );
}
